/* The mobile app's one call.
 *
 * `/api/mobile/summary` answers with everything the seven phone screens
 * draw, for one month — see backend/app/services/mobile.py. The keys are
 * short because the payload is ~60 KB and goes over a mill's mobile data;
 * the comments here are what each one means.
 */

import { request } from './api'

export interface MProdItem {
  /** 'U1' or 'U2'. */
  u: string
  /** The day sheet's whole label, e.g. '32 KW · Karded Warp'. */
  n: string
  /** Its two halves: the count, and the quality. */
  c: string
  q: string
  /** Machines on the line, averaged over the day's shifts. */
  m: number
  /** Kilograms: SITRA target, actual, and the day's waste at this line's
   *  share of output (the tracker books waste for the plant, not per count). */
  t: number
  a: number
  w: number
  /** Grams per spindle per shift — target and actual. */
  tg: number | null
  ag: number | null
}

export interface MProdDay {
  d: number
  dow: string
  /** Plant kilograms and the headline efficiency, actual against SITRA. */
  t: number
  a: number
  w: number
  e: number
  /** Each unit's efficiency, and each unit's kilograms. */
  u1: number | null
  u2: number | null
  k1: number | null
  k2: number | null
  /** Meter readings and units per kilo; null on a day with no reading. */
  eb: number | null
  solar: number | null
  ukg: number | null
  items: MProdItem[]
  /** Stoppages as a percentage of each unit's available machine minutes. */
  stop: { c: string; u1: number | null; u2: number | null }[]
}

export interface MStockDay {
  d: number
  /** Closing totals across every count / every waste type. */
  yarn: number
  waste: number
  prod: number
  disp: number
}

export interface MSales {
  empty: boolean
  /** The month actually in focus — the register's own latest when it does
   *  not reach the tracker's month. */
  mk: string
  label: string
  months: { m: string; mk: string; lab: string; kg: number; v: number; n: number }[]
  byCount: {
    c: string; n: number; bags: number; kg: number; v: number
    avg: number
    /** The month's own price spread for the count, ₹ per kg; null when no
     *  line in the month carried a price. */
    lo: number | null; hi: number | null
  }[]
  daily: { d: number; kg: number }[]
  recent: {
    no: string; d: string; c: string; cust: string
    bags: number; kg: number; v: number; p: number | null
  }[]
}

export interface MobileSummary {
  empty: boolean
  /** The tracker month everything is keyed to, and the months on file. */
  month: string | null
  label: string
  months: string[]
  /** The efficiency benchmark from the server's config. */
  bench: number
  candyKg: number

  prod: {
    month: string; mk: string; months: string[]
    bench: number
    /** Frames installed across both units. */
    machines: number
    /** The month's meters, as the desktop Production page states them.
     *  `ukg` is read over the metered days alone, so it is the server's
     *  figure and not the totals divided here. */
    power: {
      eb: number; solar: number
      ukg: number | null
      meteredDays: number; meteredKg: number
    }
    days: MProdDay[]
  }

  stock: {
    days: MStockDay[]
    /** The last date on the register, and the month it belongs to. */
    last: string | null
    label: string
    /** Closing yarn against the month's average dispatch; null when
     *  nothing has been dispatched yet. */
    cover: number | null
    yarn: {
      c: string; open: number; prod: number; disp: number; close: number
      /** The month's produced and dispatched, not just the last day's. */
      mprod: number; mdisp: number
    }[]
    waste: { type: string; dept: string; prod: number; sold: number; close: number }[]
  }

  sales: MSales
  wasteSales: MSales

  purch: {
    empty: boolean
    mk: string; label: string
    months: {
      mk: string; lab: string; n: number; bales: number; kg: number
      v: number; candy: number | null
    }[]
    suppliers: { s: string; bales: number; kg: number; v: number; candy: number | null }[]
    bills: {
      d: string; sup: string; type: string; st: string
      bales: number; kg: number; candy: number | null; v: number
    }[]
  }

  /** The latest CAI lint rate per variety. Each carries its own `d`: the
   *  varieties are not all quoted on the same day. */
  rates: {
    k: string; label: string; region: string; std: string
    mm: number | null; last: number; d: string
    wk: number | null; mo: number | null
    trend: number[]
  }[]

  hr: {
    empty: boolean
    mk: string; label: string
    /** 'September 2026 · Workers + staff'. */
    month: string
    /** Heads on the roll and the staffing requirement, both registers. */
    register: number
    req: number
    avgPresent: number
    avgHrs: number
    dayCount: number
    totals: {
      presentDays: number; late: number; miss: number
      flagged: number; short: number; ot: number; markedA: number
    }
    days: { d: number; dow: string; p: number; late: number; miss: number; ot: number }[]
    depts: {
      n: string; req: number; active: number; avg: number
      /** Attendance and late arrivals as percentages; misses as a count. */
      att: number; late: number; miss: number; ot: number
    }[]
  }
}

export const mobileApi = {
  summary: (month?: string) =>
    request<MobileSummary>('/mobile/summary' + (month ? `?month=${month}` : '')),
}
